import { useMemo } from 'react';
import {
  Box,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { toast } from 'sonner';

import { usesUrlaStyleSections } from '@/config/loanProductRegistry';
import {
  usePatchLoanAgencyFieldsMutation,
  usePatchLoanProductTypeMutation,
} from '@/apis/extended/loanApplicationApi';
import { useGetCurrentUserQuery } from '@/apis/extended/userApi';
import type {
  LoanAgencyFields,
  LoanApplication,
  LoanProductType,
} from '@/types/LoanApplicationType';
import { parseLoanPatchError } from './loanSectionForm';
import { LoanSectionFormFooter } from './LoanSectionFormFooter';
import { MoneyField } from './MoneyField';
import { useLoanSectionForm } from './useLoanSectionForm';

type Props = {
  application: LoanApplication;
  readOnly?: boolean;
};

const LOAN_PURPOSES = ['Purchase', 'Refinance', 'Other'] as const;
export const DOWN_PAYMENT_SOURCES = [
  'Checking / savings',
  'Gift from family',
  'Sale of current home',
  'Retirement account',
  'Stocks / investments',
  'Down payment assistance',
  'Other',
] as const;
const LOAN_TERMS = [30, 25, 20, 15, 10] as const;
const OCCUPANCY = [
  'Primary residence',
  'Second home',
  'Investment property',
] as const;
const AMORT = ['Fixed rate', 'ARM', 'Other'] as const;

const PROGRAM_OPTIONS: { value: LoanProductType; label: string }[] = [
  { value: 'conventional', label: 'Conventional' },
  { value: 'fha', label: 'FHA' },
  { value: 'va', label: 'VA' },
  { value: 'usda', label: 'USDA' },
];

const emptyAgency = (): LoanAgencyFields => ({
  loanPurpose: '',
  occupancyType: '',
  amortizationType: '',
  loanTermYears: null,
  loanAmount: null,
  estimatedPropertyValue: null,
  monthlyMiEstimate: null,
  vaFundingFeeExempt: false,
  fhaCaseNumber: '',
  creditScore: null,
  purchasePrice: null,
  downPaymentPercent: null,
  downPaymentAmount: null,
  earnestMoneyDeposit: null,
  downPaymentSource: '',
  refinanceType: '',
  cashOutAmount: null,
});

const round2 = (n: number) => Math.round(n * 100) / 100;

const usd = (n: number) =>
  n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });

/**
 * Loan amount and value follow from what the borrower already knows — the
 * price and down payment on a purchase, the mortgage balance and any cash out
 * on a refinance — instead of asking them to work it out.
 */
export function computeLoanTerms(
  fields: LoanAgencyFields,
  flowProfile: string | undefined,
  currentMortgageBalance: number | null
): Pick<LoanAgencyFields, 'loanAmount' | 'estimatedPropertyValue'> {
  if (flowProfile === 'refinance') {
    if (currentMortgageBalance == null) {
      return {
        loanAmount: fields.loanAmount ?? null,
        estimatedPropertyValue: fields.estimatedPropertyValue ?? null,
      };
    }
    const cashOut =
      fields.refinanceType === 'cash_out' ? fields.cashOutAmount ?? 0 : 0;
    return {
      loanAmount: currentMortgageBalance + cashOut,
      estimatedPropertyValue: fields.estimatedPropertyValue ?? null,
    };
  }
  if (fields.purchasePrice) {
    const dp = fields.downPaymentAmount ?? 0;
    return {
      loanAmount: Math.max(0, fields.purchasePrice - dp),
      estimatedPropertyValue: fields.purchasePrice,
    };
  }
  return {
    loanAmount: fields.loanAmount ?? null,
    estimatedPropertyValue: fields.estimatedPropertyValue ?? null,
  };
}

export function AgencyLoanTermsSection({ application, readOnly }: Props) {
  const [patchAgency, { isLoading }] = usePatchLoanAgencyFieldsMutation();
  const [patchProductType, { isLoading: ptLoading }] =
    usePatchLoanProductTypeMutation();

  const { data: user } = useGetCurrentUserQuery();
  const profileDownPayment = user?.loanInfo?.downPayment;

  const initialAgency = useMemo(() => {
    const base: LoanAgencyFields = {
      ...emptyAgency(),
      ...(application.agencyFields || {}),
    };
    const isPurchaseFile =
      application.flowProfile === 'purchase' ||
      application.flowProfile === 'investor_dscr';
    // Down payment from the pre-qualification, unless they've set one here.
    if (
      isPurchaseFile &&
      base.downPaymentAmount == null &&
      profileDownPayment != null
    ) {
      base.downPaymentAmount = profileDownPayment;
    }
    if (
      base.purchasePrice &&
      base.downPaymentAmount != null &&
      base.downPaymentPercent == null
    ) {
      base.downPaymentPercent = round2(
        (base.downPaymentAmount / base.purchasePrice) * 100
      );
    }
    return base;
  }, [
    application._id,
    application.agencyFields,
    application.updatedAt,
    application.flowProfile,
    profileDownPayment,
  ]);

  const {
    draft: local,
    setDraft: setLocal,
    isDirty,
    markSaved,
    resetDraft,
    readOnly: formReadOnly,
  } = useLoanSectionForm({
    sectionKey: 'agency-loan-terms',
    applicationId: application._id,
    applicationUpdatedAt: application.updatedAt,
    initialValue: initialAgency,
    readOnly,
  });

  const flow = application.flowProfile;
  const isRefinance = flow === 'refinance';
  const isPurchase = flow === 'purchase' || flow === 'investor_dscr';
  const currentMortgageBalance =
    application.refinanceFields?.currentLoanBalance ??
    application.property?.amountExistingLiens ??
    null;
  const computed = computeLoanTerms(
    local,
    flow,
    currentMortgageBalance ?? null
  );

  const setPrice = (price: number | null) =>
    setLocal((s) => {
      const pct = s.downPaymentPercent;
      return {
        ...s,
        purchasePrice: price,
        downPaymentAmount:
          price != null && pct != null
            ? Math.round((price * pct) / 100)
            : s.downPaymentAmount,
      };
    });
  const setDownPct = (pct: number | null) =>
    setLocal((s) => ({
      ...s,
      downPaymentPercent: pct,
      downPaymentAmount:
        pct != null && s.purchasePrice
          ? Math.round((s.purchasePrice * pct) / 100)
          : s.downPaymentAmount,
    }));
  const setDownAmount = (amt: number | null) =>
    setLocal((s) => ({
      ...s,
      downPaymentAmount: amt,
      downPaymentPercent:
        amt != null && s.purchasePrice
          ? round2((amt / s.purchasePrice) * 100)
          : s.downPaymentPercent,
    }));

  const canPickProgram =
    application.flowProfile !== 'investor_dscr' &&
    usesUrlaStyleSections(application.productType, application.flowProfile);

  const saveSection = async () => {
    if (formReadOnly) return;
    const next: LoanAgencyFields = {
      ...local,
      ...computed,
      loanPurpose: isRefinance
        ? 'Refinance'
        : isPurchase
        ? 'Purchase'
        : local.loanPurpose,
      cashOutAmount:
        isRefinance && local.refinanceType === 'cash_out'
          ? local.cashOutAmount ?? null
          : null,
    };
    try {
      await patchAgency({
        id: application._id,
        ...next,
      }).unwrap();
      markSaved(next);
      toast.success('Saved');
    } catch (e: unknown) {
      toast.error(parseLoanPatchError(e));
    }
  };

  const disabled = formReadOnly || isLoading;

  const onProductTypeChange = async (next: LoanProductType) => {
    if (readOnly) return;
    try {
      await patchProductType({
        id: application._id,
        productType: next,
      }).unwrap();
      toast.success('Loan program updated');
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'data' in e
          ? String((e as { data?: { error?: string } }).data?.error)
          : 'Could not update program';
      toast.error(msg);
    }
  };

  return (
    <div>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {isRefinance
          ? 'What you want from the new loan. Your loan amount follows from your current balance and any cash out.'
          : isPurchase
          ? 'The price and how much you are putting down. We work out the loan amount for you.'
          : 'Loan purpose, occupancy and terms.'}
      </Typography>
      <Grid container spacing={2}>
        {isRefinance && (
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              What do you want this refinance to do?
            </Typography>
            <ToggleButtonGroup
              color="primary"
              exclusive
              size="small"
              value={local.refinanceType || 'rate_and_term'}
              disabled={disabled}
              onChange={(_, v) =>
                v &&
                setLocal((s) => ({
                  ...s,
                  refinanceType: v as LoanAgencyFields['refinanceType'],
                }))
              }
              sx={{ flexWrap: 'wrap' }}
            >
              <ToggleButton value="rate_and_term">
                Lower my rate or payment
              </ToggleButton>
              <ToggleButton value="cash_out">Take cash out</ToggleButton>
            </ToggleButtonGroup>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.75 }}
            >
              {local.refinanceType === 'cash_out'
                ? 'Cash-out refinance: borrow more than you owe and receive the difference at closing.'
                : 'Rate & term refinance: replace your current loan without taking cash out.'}
            </Typography>
          </Grid>
        )}
        {canPickProgram && (
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              select
              label="Loan program"
              value={application.productType}
              disabled={readOnly || ptLoading}
              onChange={(e) =>
                void onProductTypeChange(e.target.value as LoanProductType)
              }
            >
              {PROGRAM_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
              {application.productType === 'other' ? (
                <MenuItem value="other">Other</MenuItem>
              ) : null}
            </TextField>
          </Grid>
        )}
        {!isRefinance && !isPurchase && (
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              select
              label="Loan purpose"
              value={local.loanPurpose || ''}
              disabled={disabled}
              onChange={(e) =>
                setLocal((s) => ({ ...s, loanPurpose: e.target.value }))
              }
            >
              <MenuItem value="">Select…</MenuItem>
              {LOAN_PURPOSES.map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            select
            label="How will you use the home?"
            value={local.occupancyType || ''}
            disabled={disabled}
            onChange={(e) =>
              setLocal((s) => ({ ...s, occupancyType: e.target.value }))
            }
          >
            <MenuItem value="">Select…</MenuItem>
            {OCCUPANCY.map((p) => (
              <MenuItem key={p} value={p}>
                {p}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Estimated credit score"
            type="number"
            value={local.creditScore ?? ''}
            disabled={disabled}
            inputProps={{ min: 300, max: 850 }}
            onChange={(e) =>
              setLocal((s) => ({
                ...s,
                creditScore:
                  e.target.value === '' ? null : Number(e.target.value),
              }))
            }
            helperText="From your pre-qualification — change it here and it updates everywhere"
          />
        </Grid>

        {isPurchase && (
          <>
            <Grid item xs={12} md={6}>
              <MoneyField
                label="Purchase price"
                value={local.purchasePrice}
                disabled={disabled}
                onChange={setPrice}
                helperText="Your offer price, or the list price for now"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <MoneyField
                label="Down payment"
                unit="percent"
                value={local.downPaymentPercent}
                disabled={disabled}
                onChange={setDownPct}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <MoneyField
                label="Down payment ($)"
                value={local.downPaymentAmount}
                disabled={disabled}
                onChange={setDownAmount}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Where is the down payment coming from?"
                value={local.downPaymentSource || ''}
                disabled={disabled}
                onChange={(e) =>
                  setLocal((s) => ({
                    ...s,
                    downPaymentSource: e.target.value,
                  }))
                }
              >
                <MenuItem value="">Select…</MenuItem>
                {DOWN_PAYMENT_SOURCES.map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <MoneyField
                label="Earnest money deposit"
                value={local.earnestMoneyDeposit}
                disabled={disabled}
                onChange={(v) =>
                  setLocal((s) => ({ ...s, earnestMoneyDeposit: v }))
                }
                helperText="The good-faith deposit in your offer, if you know it"
              />
            </Grid>
          </>
        )}

        {isRefinance && (
          <>
            <Grid item xs={12} md={6}>
              <MoneyField
                label="Estimated home value"
                value={local.estimatedPropertyValue}
                disabled={disabled}
                onChange={(v) =>
                  setLocal((s) => ({ ...s, estimatedPropertyValue: v }))
                }
              />
            </Grid>
            {local.refinanceType === 'cash_out' && (
              <Grid item xs={12} md={6}>
                <MoneyField
                  label="Cash out amount"
                  value={local.cashOutAmount}
                  disabled={disabled}
                  onChange={(v) =>
                    setLocal((s) => ({ ...s, cashOutAmount: v }))
                  }
                  helperText="How much you want to receive at closing"
                />
              </Grid>
            )}
            {currentMortgageBalance == null && (
              <Grid item xs={12} md={6}>
                <MoneyField
                  label="Loan amount"
                  value={local.loanAmount}
                  disabled={disabled}
                  onChange={(v) => setLocal((s) => ({ ...s, loanAmount: v }))}
                  helperText="Add your current loan balance in “Current loan & refinance” and we'll calculate this"
                />
              </Grid>
            )}
          </>
        )}

        {!isPurchase && !isRefinance && (
          <>
            <Grid item xs={12} md={6}>
              <MoneyField
                label="Loan amount"
                value={local.loanAmount}
                disabled={disabled}
                onChange={(v) => setLocal((s) => ({ ...s, loanAmount: v }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <MoneyField
                label="Est. property value"
                value={local.estimatedPropertyValue}
                disabled={disabled}
                onChange={(v) =>
                  setLocal((s) => ({ ...s, estimatedPropertyValue: v }))
                }
              />
            </Grid>
          </>
        )}

        {(isPurchase || (isRefinance && currentMortgageBalance != null)) &&
          computed.loanAmount != null && (
            <Grid item xs={12}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  bgcolor: 'action.hover',
                }}
              >
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={{ xs: 0.5, sm: 3 }}
                >
                  <Typography variant="body2">
                    Loan amount: <strong>{usd(computed.loanAmount)}</strong>
                  </Typography>
                  {isRefinance ? (
                    <Typography variant="body2" color="text.secondary">
                      Current balance {usd(currentMortgageBalance ?? 0)}
                      {local.refinanceType === 'cash_out'
                        ? ` + cash out ${usd(local.cashOutAmount ?? 0)}`
                        : ''}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {usd(local.purchasePrice ?? 0)} price −{' '}
                      {usd(local.downPaymentAmount ?? 0)} down
                    </Typography>
                  )}
                  {computed.estimatedPropertyValue && computed.loanAmount ? (
                    <Typography variant="body2" color="text.secondary">
                      Loan-to-value{' '}
                      <strong>
                        {Math.round(
                          (computed.loanAmount /
                            computed.estimatedPropertyValue) *
                            100
                        )}
                        %
                      </strong>
                      {application.productType === 'conventional' &&
                      computed.loanAmount / computed.estimatedPropertyValue >
                        0.8
                        ? ' · mortgage insurance usually applies above 80%'
                        : ''}
                    </Typography>
                  ) : null}
                </Stack>
              </Box>
            </Grid>
          )}

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            select
            label="Rate type"
            value={local.amortizationType || ''}
            disabled={disabled}
            onChange={(e) =>
              setLocal((s) => ({ ...s, amortizationType: e.target.value }))
            }
          >
            <MenuItem value="">Select…</MenuItem>
            {AMORT.map((p) => (
              <MenuItem key={p} value={p}>
                {p}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            select
            label="Loan term"
            value={local.loanTermYears ?? ''}
            disabled={disabled}
            onChange={(e) =>
              setLocal((s) => ({
                ...s,
                loanTermYears:
                  e.target.value === '' ? null : Number(e.target.value),
              }))
            }
          >
            <MenuItem value="">Select…</MenuItem>
            {LOAN_TERMS.map((t) => (
              <MenuItem key={t} value={t}>
                {t} years
              </MenuItem>
            ))}
            {local.loanTermYears &&
            !LOAN_TERMS.includes(
              local.loanTermYears as (typeof LOAN_TERMS)[number]
            ) ? (
              <MenuItem value={local.loanTermYears}>
                {local.loanTermYears} years
              </MenuItem>
            ) : null}
          </TextField>
        </Grid>
        {(application.productType === 'fha' ||
          application.productType === 'conventional') && (
          <Grid item xs={12} md={6}>
            <MoneyField
              label="Monthly mortgage insurance estimate (if any)"
              value={local.monthlyMiEstimate}
              disabled={disabled}
              onChange={(v) =>
                setLocal((s) => ({ ...s, monthlyMiEstimate: v }))
              }
            />
          </Grid>
        )}
        {application.productType === 'fha' && (
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="FHA case # (if assigned)"
              value={local.fhaCaseNumber || ''}
              disabled={disabled}
              onChange={(e) =>
                setLocal((s) => ({ ...s, fhaCaseNumber: e.target.value }))
              }
            />
          </Grid>
        )}
        {application.productType === 'va' && (
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(local.vaFundingFeeExempt)}
                  disabled={disabled}
                  onChange={(_, v) =>
                    setLocal((s) => ({ ...s, vaFundingFeeExempt: v }))
                  }
                />
              }
              label="VA funding fee exempt (disability / eligible surplus)"
            />
          </Grid>
        )}
      </Grid>
      <LoanSectionFormFooter
        readOnly={formReadOnly}
        isDirty={isDirty}
        isSaving={isLoading}
        saveLabel="Save loan details"
        onSave={() => void saveSection()}
        onCancel={resetDraft}
      />
    </div>
  );
}
